import type { Sequence, SequenceStep } from '@/constants/crm'

import { useState } from 'react'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import Tag from '@/components/ui/tag'
import DetailSection from '@/components/companies/detail/detail-section'
import StepsEditor, { cleanSteps } from '@/components/sequences/steps-editor'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ownerByName } from '@/constants/companies'
import { sequenceStatusMeta } from '@/constants/crm'
import { apiRequest } from '@/lib/client-api'
import { useCrmStore } from '@/stores/crm-store-provider'
import MailIcon from '@/assets/images/companies/sidebar/mail.svg'
import XIcon from '@/assets/images/companies/detail/x.svg'

import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

interface SequenceBodyProps {
    sequence: Sequence
}

export function nextStatusAction(status: string): { label: string, status: string } {
    if (status === 'Active') return { label: "Pause", status: 'Paused' }
    if (status === 'Paused') return { label: "Resume", status: 'Active' }

    return { label: "Activate", status: 'Active' }
}

function SequenceBody({ sequence }: SequenceBodyProps) {
    const upsertSequence = useCrmStore(state => state.upsertSequence)
    const showNotice = useCrmStore(state => state.showNotice)
    const openProfile = useCrmStore(state => state.openProfile)
    const [steps, setSteps] = useState<SequenceStep[]>(sequence.steps.length > 0 ? sequence.steps : [{ day: 0, channel: 'Email', subject: '' }])
    const [saving, setSaving] = useState(false)
    const owner = ownerByName(sequence.owner)
    const status = sequenceStatusMeta(sequence.status)
    const action = nextStatusAction(sequence.status)
    const dirty = JSON.stringify(cleanSteps(steps)) !== JSON.stringify(sequence.steps)

    async function save(patch: Record<string, unknown>, message: string) {
        setSaving(true)

        const result = await apiRequest<{ sequence: Sequence }>(`/api/sequences/${sequence.id}`, 'PATCH', patch)

        setSaving(false)

        if (!result.ok) {
            showNotice('error', result.message)

            return
        }

        upsertSequence(result.data.sequence)
        showNotice('success', message)
    }

    function saveSteps() {
        const cleaned = cleanSteps(steps)

        if (cleaned.length === 0) {
            showNotice('error', "Keep at least one step with a subject.")

            return
        }

        setSteps(cleaned)
        save({ steps: cleaned }, "Steps saved.")
    }

    return (
        <>
            <ScrollArea className='min-h-0 flex-1'>
                <div className='flex flex-col gap-3 p-5 shadow-[inset_0_-1px_0_var(--line-strong)]'>
                    <div className='flex flex-wrap items-center gap-2'>
                        <h2 className='min-w-0 truncate' data-mdlfy-cms={`email_sequences:${sequence.id}:name`}>
                            {sequence.name}
                        </h2>
                        <Tag tone={status.tone} size='sm' className='text-[12px]'>
                            {status.value}
                        </Tag>
                    </div>
                    <p className='text-soft' data-mdlfy-cms={`email_sequences:${sequence.id}:description`}>
                        {sequence.description}
                    </p>
                    <Button
                        variant='ghost'
                        size='none'
                        onClick={() => openProfile(owner.name)}
                        className='caption-style text-foreground -mx-1.5 w-fit gap-1.5 px-1.5 py-1 font-normal'
                    >
                        <Avatar src={owner.avatar} alt='' />
                        <span data-mdlfy-cms={`email_sequences:${sequence.id}:owner`}>
                            {sequence.owner}
                        </span>
                    </Button>
                </div>

                <DetailSection title="Performance">
                    <div className='grid grid-cols-3 gap-2'>
                        {[
                            { label: "Enrolled", value: String(sequence.enrolled) },
                            { label: "Open rate", value: `${sequence.openRate}%` },
                            { label: "Reply rate", value: `${sequence.replyRate}%` }
                        ].map(stat => (
                            <div key={stat.label} className='border-line-strong flex flex-col gap-3 rounded-lg border p-[11px]'>
                                <span className='caption-style text-soft block'>{stat.label}</span>
                                <span className='lead-style block tabular-nums'>{stat.value}</span>
                            </div>
                        ))}
                    </div>
                </DetailSection>

                <DetailSection title="Steps" className='shadow-none'>
                    <StepsEditor idPrefix={`sequence-${sequence.id}`} steps={steps} onChange={setSteps} disabled={saving} />
                </DetailSection>
            </ScrollArea>

            <SheetFooter>
                <Button
                    variant='subtle'
                    size='sm'
                    disabled={saving}
                    onClick={() => save({ status: action.status }, `Sequence ${action.status === 'Paused' ? 'paused' : 'activated'}.`)}
                >
                    {action.label}
                </Button>
                <Button variant='primary' size='sm' onClick={saveSteps} disabled={saving || !dirty}>
                    {saving ? "Saving…" : "Save steps"}
                </Button>
            </SheetFooter>
        </>
    )
}

export default function SequenceDetail() {
    const sequenceId = useCrmStore(state => state.sequenceId)
    const sequences = useCrmStore(state => state.sequences)
    const closeSequence = useCrmStore(state => state.closeSequence)
    const sequence = sequences.find(item => item.id === sequenceId)

    return (
        <Sheet open={sequence !== undefined} onOpenChange={open => !open && closeSequence()}>
            <SheetContent side='right' className='sm:w-[560px] sm:max-w-[560px]'>
                <SheetHeader>
                    <div className='flex items-center gap-2'>
                        <MailIcon aria-hidden className='text-icon size-3.5' />
                        <SheetTitle>Email Sequence</SheetTitle>
                    </div>
                    <SheetDescription className='sr-only'>
                        Sequence status, performance and steps
                    </SheetDescription>
                    <SheetClose asChild>
                        <Button variant='ghost' size='icon-sm' className='-mr-1' aria-label="Close sequence">
                            <XIcon aria-hidden className='text-foreground size-4' />
                        </Button>
                    </SheetClose>
                </SheetHeader>
                {sequence && <SequenceBody key={sequence.id} sequence={sequence} />}
            </SheetContent>
        </Sheet>
    )
}
